import React from 'react'
import { Button, ButtonProps, CircularProgress } from '@mui/material'
import withCapabilities from '../../hoc/withCapabilities'
import theme from '../../theme'

/**
 * ActionButton Component
 *
 * A customizable button component with predefined styles for different actions and sizes.
 *
 * Available button types:
 * - 'primary' (default): Primary action button (contained, primary color)
 * - 'approve': Approval button (outlined, success color)
 * - 'reject': Rejection button (outlined, error color)
 * - 'secondary': Secondary action button (contained, secondary color)
 *
 * Available sizes:
 * - 'small': Small-sized button
 * - 'medium': Medium-sized button
 * - 'large': Large-sized button
 * - 'fullWidth' (default): Full-width button
 *
 * Usage:
 * <ActionButton buttonType="approve" size="medium">Approve</ActionButton>
 */

interface ActionButtonProps extends Omit<ButtonProps, 'size'> {
  buttonType?: 'primary' | 'approve' | 'reject' | 'secondary' | 'secondaryAlert'
  size?: 'small' | 'medium' | 'large' | 'fullWidth'
  isLoading?: boolean
  loading?: boolean
  loadingLabel?: string
  paginationLoading?: boolean
}

const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  buttonType = 'primary',
  size: buttonSize = 'fullWidth',
  isLoading = false,
  loading = false,
  loadingLabel,
  paginationLoading = false,
  ...props
}) => {
  let variant: 'contained' | 'outlined'
  let color: 'primary' | 'secondary' | 'error' | 'success'
  let backgroundColor = undefined
  let hoverBackgroundColor = undefined
  switch (buttonType) {
    case 'approve':
      variant = 'contained'
      color = 'success'
      break
    case 'reject':
      variant = 'contained'
      color = 'error'
      break
    case 'secondary':
      variant = 'outlined'
      color = 'primary'
      backgroundColor = `${theme.palette.background.light}80` // 50% transparency
      hoverBackgroundColor = `${theme.palette.background.light}ab` // 70% transparency
      break
    case 'secondaryAlert':
      variant = 'outlined'
      color = 'error'
      backgroundColor = `${theme.palette.background.light}80` // 50% transparency
      hoverBackgroundColor = `${theme.palette.background.light}ab` // 70% transparency
      break
    default: //primary
      variant = 'contained'
      color = 'primary'
  }

  const busy = isLoading || loading

  return (
    <Button
      {...props}
      variant={props.variant || variant}
      color={props.color || color}
      fullWidth={buttonSize === 'fullWidth' || props.fullWidth}
      size={buttonSize === 'fullWidth' ? undefined : buttonSize}
      disabled={props.disabled || busy}
      aria-busy={busy || undefined}
      sx={[
        {
          borderWidth: 2,
          ...((props.variant || variant) === 'contained' ? { borderStyle: 'solid', borderColor: 'transparent' } : {}),
          '&:hover': { borderWidth: 2 },
        },
        ...(backgroundColor
          ? [
              {
                backgroundColor,
                '&:hover': { backgroundColor: hoverBackgroundColor },
              },
            ]
          : []),
        busy &&
          paginationLoading && {
            '&.Mui-disabled': {
              opacity: 0.7,
              color: 'primary.contrastText',
              backgroundColor: 'primary.light',
            },
          },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      {busy && loadingLabel ? loadingLabel : children}
      {busy && <CircularProgress size={20} color="inherit" sx={{ ml: 1, flexShrink: 0 }} />}
    </Button>
  )
}

export default withCapabilities(ActionButton)
